import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from 'react-query';
import { apiService } from '../../../../utils/api';
import { parsePATFile, Pattern } from '@av-platform/rendering-core';
import { PaginatedResponse } from '../../../../types/global';
import type {
  SolutionPatternResponse,
  LoadedSolutionPattern,
  UploadPatternParams,
  MaterialPatternRequest,
  UpdateMaterialPatternParams,
  MaterialPatternResponse,
} from '../types';
import { SOLID_PATTERN_DATA } from '../types';

// Re-export types for backward compatibility
export type {
  SolutionPatternResponse,
  LoadedSolutionPattern,
  UploadPatternParams,
  MaterialPatternRequest,
  UpdateMaterialPatternParams,
  MaterialPatternResponse,
} from '../types';

/**
 * Fetch .pat file content from URL and parse it
 */
async function fetchAndParsePatternFile(
  fileUrl: string
): Promise<{ patterns: Pattern[]; content: string }> {
  const response = await fetch(fileUrl);
  if (!response.ok) {
    throw new Error(`Failed to fetch pattern file: ${response.statusText}`);
  }
  const content = await response.text();
  const patterns = parsePATFile(content);
  return { patterns, content };
}

/**
 * Hook to fetch solution patterns with pagination and file content loading
 */
export const useGetSolutionPatterns = (pageSize: number = 20) => {
  return useInfiniteQuery({
    queryKey: ['solution-patterns'],
    queryFn: async ({ pageParam = 1 }) => {
      // Fetch paginated list
      const { data } = await apiService.get<PaginatedResponse<SolutionPatternResponse>>(
        `/api/v1/solution-patterns/?page=${pageParam}&page_size=${pageSize}`
      );

      // Load and parse each pattern file
      const loadedPatterns: LoadedSolutionPattern[] = [];

      for (const item of data.results) {
        try {
          const { patterns, content } = await fetchAndParsePatternFile(item.file);

          // Each .pat file can contain multiple patterns
          // We'll use the first pattern and include the item metadata
          if (patterns.length > 0) {
            loadedPatterns.push({
              id: item.id,
              title: item.title || patterns[0].name,
              pattern: patterns[0],
              fileContent: content,
              isPublic: item.is_public,
            });
          }
        } catch (error) {
          console.error(`Failed to load pattern ${item.id}:`, error);
          // Continue with other patterns even if one fails
        }
      }

      return {
        patterns: loadedPatterns,
        nextPage: data.next ? pageParam + 1 : undefined,
        totalCount: data.count,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextPage,
    refetchOnWindowFocus: false,
  });
};

/**
 * Simple hook to fetch all patterns (non-paginated, for smaller datasets)
 */
export const useGetAllSolutionPatterns = () => {
  return useQuery({
    queryKey: ['solution-patterns-all'],
    queryFn: async () => {
      // Fetch all patterns (assuming reasonable amount)
      const { data } = await apiService.get<PaginatedResponse<SolutionPatternResponse>>(
        `/api/v1/solution-patterns/?page_size=100`
      );

      // Load and parse each pattern file
      const loadedPatterns: LoadedSolutionPattern[] = [];

      for (const item of data.results) {
        try {
          const { patterns, content } = await fetchAndParsePatternFile(item.file);

          if (patterns.length > 0) {
            loadedPatterns.push({
              id: item.id,
              title: item.title || patterns[0].name,
              pattern: patterns[0],
              fileContent: content,
              isPublic: item.is_public,
            });
          }
        } catch (error) {
          console.error(`Failed to load pattern ${item.id}:`, error);
        }
      }

      return {
        patterns: loadedPatterns,
        totalCount: data.count,
      };
    },
    refetchOnWindowFocus: false,
  });
};

/**
 * Mutation hook to upload a .PAT file
 */
export const useUploadSolutionPattern = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ file, title }: UploadPatternParams) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', title);

      const { data } = await apiService.post<SolutionPatternResponse>(
        '/api/v1/solution-patterns/upload/',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      return data;
    },
    onSuccess: () => {
      // Invalidate patterns queries to refetch with new pattern
      queryClient.invalidateQueries(['solution-patterns']);
      queryClient.invalidateQueries(['solution-patterns-all']);
    },
  });
};

/**
 * Mutation hook to update material pattern for a solution
 */
export const useUpdateMaterialPattern = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ solutionId, data }: UpdateMaterialPatternParams) => {
      const response = await apiService.post<MaterialPatternRequest>(
        `/api/v1/solution/${solutionId}/material-pattern/`,
        data
      );

      return response.data;
    },
    onSuccess: (_, variables) => {
      // Invalidate solution query to refetch with updated material pattern
      queryClient.invalidateQueries(['solution', variables.solutionId]);
    },
  });
};

/**
 * Hook to load patterns from material_pattern using react-query
 */
export const useMaterialPattern = (materialPattern: MaterialPatternResponse | null | undefined) => {
  const fgFile = materialPattern?.fg_pattern?.file;
  const bgFile = materialPattern?.bg_pattern?.file;

  const fgQuery = useQuery({
    queryKey: ['pattern-file', fgFile],
    queryFn: async () => {
      if (!fgFile) return null;
      const { patterns } = await fetchAndParsePatternFile(fgFile);
      return patterns[0] || null;
    },
    enabled: !!fgFile,
    staleTime: Infinity, // Patterns are static assets
  });

  const bgQuery = useQuery({
    queryKey: ['pattern-file', bgFile],
    queryFn: async () => {
      if (!bgFile) return null;
      const { patterns } = await fetchAndParsePatternFile(bgFile);
      return patterns[0] || null;
    },
    enabled: !!bgFile,
    staleTime: Infinity,
  });

  // When material_pattern exists but fg/bg_pattern is null, treat as solid fill
  const fgResult = materialPattern
    ? materialPattern.fg_pattern
      ? fgQuery.data ?? null
      : SOLID_PATTERN_DATA
    : null;

  const bgResult = materialPattern
    ? materialPattern.bg_pattern
      ? bgQuery.data ?? null
      : SOLID_PATTERN_DATA
    : null;

  return {
    foregroundPattern: fgResult,
    backgroundPattern: bgResult,
    loadingPatterns: fgQuery.isLoading || bgQuery.isLoading,
  };
};
